'use client';

import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { isSemesterId, SEMESTERS } from '@/app/lib/semesters';
import { acquirePhysicalWrite, getPhysicalWrite, releasePhysicalWrite, subscribePhysicalWrite, SCHEDULE_WRITE_SCOPE } from '../hooks/physicalWriteSlot';

export interface ScheduleSemesterIdentity {
  ownerId: string;
  scheduleId: string;
  routeKey: string;
}

export interface ScheduleSemesterChangeOptions {
  ownerId: string | null;
  scheduleId: string | null;
  routeKey: string;
  currentTerm: string;
  onCommit: (identity: ScheduleSemesterIdentity, term: string) => void;
  writeTerm: (scheduleId: string, term: string) => Promise<unknown>;
}

// One tab/module owns one physical write at a time, including across remounts.
// No owner or schedule data outlives its hook instance.


/** Serializes physical writes; retired contexts cannot publish their completion. */
export function useScheduleSemesterChange({
  ownerId, scheduleId, routeKey, currentTerm, onCommit, writeTerm,
}: ScheduleSemesterChangeOptions) {
  // A new object also distinguishes A -> B -> A from the original A lifetime.
  const context = useMemo(() => ({ ownerId, scheduleId, routeKey, currentTerm }), [ownerId, scheduleId, routeKey, currentTerm]);
  const committed = useRef<{ context: typeof context; onCommit: typeof onCommit; writeTerm: typeof writeTerm } | null>(null);
  const lifetime = useRef<symbol | null>(null);
  const [state, setState] = useState<{ isPending: boolean; error: string | null }>(() => ({ isPending: getPhysicalWrite(SCHEDULE_WRITE_SCOPE) !== undefined, error: null }));

  useLayoutEffect(() => { committed.current = { context, onCommit, writeTerm }; });
  useLayoutEffect(() => {
    const epoch = Symbol('schedule-semester');
    lifetime.current = epoch;
    setState({ isPending: getPhysicalWrite(SCHEDULE_WRITE_SCOPE) !== undefined, error: null });
    return () => {
      if (lifetime.current === epoch) lifetime.current = null;
      // The write cannot be cancelled. Keep its physical lock until settlement,
      // including when another owner/schedule/route/term becomes current.
    };
  }, [context]);

  useLayoutEffect(() => {
    const updatePending = () => {
      if (lifetime.current !== null) setState(previous => ({ ...previous, isPending: getPhysicalWrite(SCHEDULE_WRITE_SCOPE) !== undefined }));
    };
    const unsubscribe = subscribePhysicalWrite(SCHEDULE_WRITE_SCOPE, updatePending);
    updatePending();
    return unsubscribe;
  }, []);

  const changeTerm = useCallback(async (term: string): Promise<boolean> => {
    const options = committed.current;
    const epoch = lifetime.current;
    if (!options || !epoch || options.context !== context || !context.ownerId || !context.scheduleId || getPhysicalWrite(SCHEDULE_WRITE_SCOPE) !== undefined) return false;
    if (!isSemesterId(term) || !SEMESTERS.some(semester => semester.id === term)) {
      setState({ isPending: false, error: 'Choose a supported semester.' });
      return false;
    }
    if (term === context.currentTerm) {
      setState({ isPending: false, error: null });
      return true;
    }

    const operation = Symbol('semester-write');
    const identity: ScheduleSemesterIdentity = { ownerId: context.ownerId, scheduleId: context.scheduleId, routeKey: context.routeKey };
    if (!acquirePhysicalWrite(SCHEDULE_WRITE_SCOPE, operation)) return false;
    setState({ isPending: true, error: null });
    const isCurrent = () => lifetime.current === epoch && committed.current?.context === context && getPhysicalWrite(SCHEDULE_WRITE_SCOPE) === operation;
    try {
      await options.writeTerm(identity.scheduleId, term);
      if (!isCurrent()) return false;
      committed.current!.onCommit(identity, term);
      // The host may synchronously commit currentTerm here. That successful
      // publication is allowed to retire this context before finally runs.
      return true;
    } catch {
      if (isCurrent()) setState({ isPending: true, error: 'Failed to change semester. Please try again.' });
      return false;
    } finally {
      releasePhysicalWrite(SCHEDULE_WRITE_SCOPE, operation);
    }
  }, [context]);

  return { changeTerm, ...state };
}
